import { useCallback } from 'react';

import type {
  AddThirdPartyCashItemRequest,
  BaseAccountRequest,
  ManagedBankRequest,
  WBankAccountView,
  WManagedAccountView,
  WManagedBankAccountView,
  WRegistryCollectionItemView,
  WRegistryCollectionSearchView,
} from '@zola/svc-web-api-ts-client';
import { ApiService, toastsActions } from '@zola-helpers/client/dist/es';

import { removeRegistryItemAction } from '@/actions/ManageRegistryActions';
import { fetchMessagesAndPlanner } from '@/actions/OverviewActions';
import {
  addCashFundToRegistry as addCashFundToWeddingRegistry,
  AddToRegistryRequest,
  addToRegistry as addToWeddingRegistry,
} from '@/actions/RegistryCollectionActions';
import { useAppDispatch } from '@/store';
import type { RegistryItemDetails } from '@/types/RegistryItemDetails';
import Logger from '@/util/logger';

import {
  ManageRegistryCollectionContext,
  AddCashFundFn,
  AddExternalCashFundFn,
  DeleteRegistryItemFn,
  UpdateRegistryItemFn,
} from './ManageRegistryCollectionContext';

const ManageWeddingRegistryCollectionProvider: React.FC<{
  registryObjectId: string | null;
}> = ({ registryObjectId, children }) => {
  const dispatch = useAppDispatch();

  const addToRegistry = useCallback(
    (
      data: AddToRegistryRequest,
      productShippingZones?: string[],
      productShippingExcludedStates?: string[]
    ) => {
      return dispatch(
        addToWeddingRegistry(data, productShippingZones, productShippingExcludedStates)
      );
    },
    [dispatch]
  );

  const updateRegistryItem: UpdateRegistryItemFn = useCallback(
    (collectionItemId, request) => {
      return ApiService.put<WRegistryCollectionItemView>(
        `/web-registry-api/v1/registryCollection/item/${collectionItemId}`,
        request
      )
        .then(() => dispatch(fetchMessagesAndPlanner()))
        .then(() => undefined);
    },
    [dispatch]
  );

  const updateExternalRegistryItem = useCallback(
    (collectionItemId?: string, requestPayload?: RegistryItemDetails) => {
      const body = {
        ...requestPayload,
        registryObjectId,
      };

      return ApiService.post<WRegistryCollectionItemView>(
        `/web-registry-api/v1/registryCollection/external-product/${collectionItemId}`,
        body
      )
        .then(() => dispatch(fetchMessagesAndPlanner()))
        .then(() => undefined);
    },
    [dispatch, registryObjectId]
  );

  const deleteRegistryItem: DeleteRegistryItemFn = useCallback(
    (itemId) => {
      return ApiService.delete<WRegistryCollectionSearchView>(
        `/web-registry-api/v1/registryCollection/delete/${itemId}`
      )
        .then(() => dispatch(removeRegistryItemAction(itemId)))
        .then(() => dispatch(toastsActions.positive({ headline: 'Item successfully removed' })))
        .then(() => dispatch(fetchMessagesAndPlanner()))
        .then(() => undefined)
        .catch((err) => {
          Logger.error(err.message, err);
          dispatch(toastsActions.negative({ headline: "Sorry we couldn't remove your item." }));
        });
    },
    [dispatch]
  );

  const addCashFundToRegistry: AddCashFundFn = useCallback(
    (request) => {
      return dispatch(addCashFundToWeddingRegistry(request));
    },
    [dispatch]
  );

  const updateCashFund = useCallback(
    (collectionItemId?: string, requestPayload?: RegistryItemDetails) => {
      const body = {
        ...requestPayload,
        registryObjectId,
      };

      return ApiService.put<WRegistryCollectionItemView>(
        `/web-registry-api/v1/registryCollection/cash-fund/${collectionItemId}`,
        body
      )
        .then(() => dispatch(fetchMessagesAndPlanner()))
        .then(() => undefined);
    },
    [dispatch, registryObjectId]
  );

  const addExternalCashFundToRegistry: AddExternalCashFundFn = useCallback(
    (request) => {
      return ApiService.post<
        WRegistryCollectionItemView,
        Omit<AddThirdPartyCashItemRequest, 'user_object_id' | 'platform' | 'registry_id'>
      >('/web-registry-api/v1/registryCollection/third-party-cash/cash-fund', request).then(
        (response) => {
          dispatch(fetchMessagesAndPlanner()).catch(() => null);
          return response;
        }
      );
    },
    [dispatch]
  );

  const updateThirdPartyCashFund = useCallback(
    (collectionItemId?: string, requestPayload?: RegistryItemDetails) => {
      const body = {
        ...requestPayload,
        registryObjectId,
      };

      return ApiService.put<WRegistryCollectionItemView>(
        `/web-registry-api/v1/registryCollection/third-party-cash/cash-fund/${collectionItemId}`,
        body
      )
        .then(() => dispatch(fetchMessagesAndPlanner()))
        .then(() => undefined);
    },
    [dispatch, registryObjectId]
  );

  const checkRegistryBankAccount = useCallback(() => {
    return ApiService.get<WBankAccountView>(
      `/web-registry-api/v1/cash-settings/${registryObjectId}/bank-account`
    );
  }, [registryObjectId]);

  const checkRegistryManagedAccount = useCallback(() => {
    return ApiService.get<WManagedAccountView>(
      `/web-registry-api/v1/cash-settings/${registryObjectId}/managed-account`
    );
  }, [registryObjectId]);

  const createRegistryManagedAccount = useCallback(
    (request: Partial<BaseAccountRequest>) => {
      const fullRequest = { ...request, registry_id: registryObjectId };
      return ApiService.post<WManagedAccountView>(
        `/web-registry-api/v1/cash-settings/${registryObjectId}/managed-account`,
        fullRequest
      );
    },
    [registryObjectId]
  );

  const addManagedBankAccount = useCallback(
    (request: ManagedBankRequest) => {
      return ApiService.post<WManagedBankAccountView>(
        `/web-registry-api/v1/cash-settings/${registryObjectId}/managed-bank`,
        request
      );
    },
    [registryObjectId]
  );

  return (
    <ManageRegistryCollectionContext.Provider
      value={{
        addToRegistry,
        updateRegistryItem,
        updateExternalRegistryItem,
        deleteRegistryItem,
        addCashFundToRegistry,
        updateCashFund,
        addExternalCashFundToRegistry,
        updateThirdPartyCashFund,
        checkRegistryBankAccount,
        createRegistryManagedAccount,
        addManagedBankAccount,
        checkRegistryManagedAccount,
      }}
    >
      {children}
    </ManageRegistryCollectionContext.Provider>
  );
};

export default ManageWeddingRegistryCollectionProvider;
